import React, { useEffect, useState } from "react";
import { useLang } from "@/i18n/LanguageContext";
import { Menu, X, Globe } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const LANG_LABEL = { pt: "PT", en: "EN", es: "ES" };
const LANG_FULL = { pt: "Português", en: "English", es: "Español" };

export default function Header() {
  const { t, lang, setLang, codes } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      setOpen(false);
    }
  };

  const navItems = [
    { id: "manifesto", label: t.nav.about },
    { id: "architecture", label: t.nav.architecture },
    { id: "services", label: t.nav.services },
    { id: "process", label: t.nav.process },
    { id: "technology", label: t.nav.technology },
    { id: "diagnostic", label: t.nav.diagnostic },
  ];

  return (
    <header
      data-testid="site-header"
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/85 backdrop-blur-xl border-b border-neutral-200/80"
          : "bg-transparent"
      }`}
    >
      <div className="sap-container flex items-center justify-between h-20">
        {/* Brand */}
        <a
          href="#top"
          data-testid="brand-logo"
          onClick={(e) => {
            e.preventDefault();
            window.scrollTo({ top: 0, behavior: "smooth" });
          }}
          className="flex items-center gap-3 group"
        >
          <img
            src="/logo-sapientia-dark.png"
            alt="Sapientia Consultores Associados"
            className="h-10 md:h-11 w-auto object-contain select-none"
            draggable={false}
          />
        </a>

        {/* Desktop nav */}
        <nav className="hidden xl:flex items-center gap-7 flex-1 justify-center px-8">
          {navItems.map((n) => (
            <button
              key={n.id}
              type="button"
              onClick={() => scrollTo(n.id)}
              data-testid={`nav-${n.id}`}
              className="whitespace-nowrap text-[11px] uppercase tracking-[0.2em] font-medium transition-colors hover:text-[color:var(--sap-gold)]"
              style={{
                color: scrolled ? "var(--sap-navy)" : "rgba(255,255,255,0.9)",
              }}
            >
              {n.label}
            </button>
          ))}
        </nav>

        {/* Right */}
        <div className="flex items-center gap-3">
          <DropdownMenu>
            <DropdownMenuTrigger
              data-testid="lang-switcher"
              className={`inline-flex items-center gap-2 px-3 py-2 text-[11px] tracking-[0.2em] uppercase border transition-colors ${
                scrolled
                  ? "border-neutral-300 text-[color:var(--sap-navy)] hover:border-[color:var(--sap-gold)]"
                  : "border-white/40 text-white hover:border-white"
              }`}
            >
              <Globe size={14} strokeWidth={1.5} />
              {LANG_LABEL[lang]}
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-[140px]">
              {codes.map((c) => (
                <DropdownMenuItem
                  key={c}
                  data-testid={`lang-option-${c}`}
                  onClick={() => setLang(c)}
                  className={c === lang ? "font-semibold" : ""}
                >
                  {LANG_FULL[c]}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <button
            type="button"
            onClick={() => scrollTo("diagnostic")}
            data-testid="header-cta"
            className="hidden md:inline-flex sap-btn-primary !py-2.5 !px-4 !text-[11px] whitespace-nowrap"
          >
            {t.nav.cta}
          </button>

          <button
            type="button"
            aria-label="Menu"
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            className="xl:hidden p-2"
            style={{ color: scrolled ? "var(--sap-navy)" : "#fff" }}
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      {open && (
        <div className="xl:hidden bg-white border-t border-neutral-200" data-testid="mobile-menu">
          <div className="sap-container py-6 flex flex-col gap-4">
            {navItems.map((n) => (
              <button
                key={n.id}
                onClick={() => scrollTo(n.id)}
                data-testid={`mobile-nav-${n.id}`}
                className="text-left text-sm uppercase tracking-[0.22em] py-2 border-b border-neutral-100 text-[color:var(--sap-navy)]"
              >
                {n.label}
              </button>
            ))}
            <button
              onClick={() => scrollTo("diagnostic")}
              className="sap-btn-primary mt-2 justify-center"
            >
              {t.nav.cta}
            </button>
          </div>
        </div>
      )}
    </header>
  );
}
